import React from 'react';
import { CardsThree } from '@phosphor-icons/react';

import { cn } from '../../../../../design-system/lib/cn';
import { Empty, Spinner } from '../../../../../design-system/components/desktop';
import { Gallery, Identity } from '../../../../../design-system/patterns/detail';
import {
  ModalField,
  ModalInput,
  ModalSelect,
  ModalTextarea,
} from '../../../../../design-system/patterns/modal-form';
import { mediaUrl } from '../../../../../utils/common';
import { WizardPanel } from '../../wizard';

/**
 * Step 1, card details. The view. Pure.
 *
 * WHAT CHANGED AND WHY
 * --------------------
 * The catalogue picker was `UI/DropDown`, one of the app's THREE select
 * implementations, rendered as an absolutely positioned overlay anchored to an
 * input. The upstream `new-listing.html` picks the card with an inline list of
 * result rows instead, and it is right: the seller is comparing printings, and
 * a comparison you have to hold open with a pointer is not a comparison. The
 * rows are inline, they stay put, and each one is a real `<button>`, so the
 * whole row is the hit target and the keyboard reaches it with Tab.
 *
 * A PICKED ROW IS ACCENT, NEVER INK. Ink is command, accent is state, and which
 * card you picked is state the system is reporting back.
 *
 * ONCE A CARD IS PICKED IT IS PREVIEWED, full art and all four identifying
 * facts, because everything below this point — condition, price, photos — is
 * the seller describing a specific printing, and they had nothing on screen
 * confirming which one they had chosen. See the block comment on it below.
 *
 * Condition keeps its per-option description, moved from a paragraph that
 * floated under the control into the field's own `hint`, which is what the hint
 * slot is for and which also gets it into `aria-describedby`.
 *
 * NO GRADES. Condition is the real five-value vocabulary the API accepts. There
 * is no PSA, BGS or CGC field on a Sleevr listing and none is implied here.
 */
export function CardDetailsStepView({
  formData,
  updateFormData,
  card,
  cardLoading = false,
  conditionOptions = [],
  finishOptions = [],
  finishDisabled = false,
  searchTerm = '',
  onSearchTermChange,
  onOpenResults,
  resultsOpen = false,
  results = [],
  loading = false,
  selectedName,
  onSelectCard,
  errors = {},
  titleRef,
}) {
  const conditionHint = conditionOptions.find((opt) => opt.value === formData.condition)
    ?.description;

  const showResults = resultsOpen && Boolean(searchTerm.trim());

  return (
    <WizardPanel
      ref={titleRef}
      step={1}
      title="Card details"
      sub="Every listing is attached to a catalogue card, which is how buyers find it and how the market price is worked out. Search, then pick the exact printing you are holding."
    >
      <ModalField
        label="Select Card"
        error={errors.card_id}
        hint="Check the card number in the bottom corner if two printings look the same."
      >
        <ModalInput
          value={selectedName || searchTerm}
          placeholder="Search card"
          autoComplete="off"
          onFocus={onOpenResults}
          onClick={onOpenResults}
          onChange={(event) => onSearchTermChange(event.target.value)}
        />
      </ModalField>

      {showResults ? (
        <div>
          {loading ? (
            <Spinner center label="Searching the catalogue" />
          ) : results.length ? (
            <div className="dsa-cl-results" role="list">
              {/* THE LIST ROLE GOES ON A WRAPPER, NOT ON THE BUTTON.
                  `role="listitem"` was written on the <button> itself, which
                  REPLACED its button role rather than adding to it: a screen
                  reader announced a list item, not something pressable, and
                  `aria-pressed` is not a state a listitem has, so which card
                  is picked was announced to nobody. The button keeps its own
                  role now and the listitem is the div around it. That div is
                  the grid item and the button is already `width: 100%`, so
                  nothing moves on screen. */}
              {results.map((card) => (
                <div key={card.id} role="listitem">
                  <button
                    type="button"
                    className={cn(
                      'dsa-cl-result',
                      card.id === formData.card_id && 'is-picked',
                    )}
                    aria-pressed={card.id === formData.card_id}
                    onClick={() => onSelectCard(card.id, card.product_name)}
                  >
                    {card.image_url ? (
                      <img
                        className="dsa-cl-result__art"
                        src={mediaUrl(card.image_url)}
                        alt=""
                        aria-hidden="true"
                      />
                    ) : (
                      <span className="dsa-cl-result__art" aria-hidden="true" />
                    )}
                    <span className="dsa-cl-result__main">
                      <span className="dsa-cl-result__title">{card.product_name}</span>
                      <span className="dsa-cl-result__meta">
                        {[card.catalog_group, card.card_number, card.rarity]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              icon={CardsThree}
              size="sm"
              title="No catalogue cards match that search"
              text="Try the card number instead of the name, or search the set it came from."
            />
          )}
        </div>
      ) : null}

      {/* THE PREVIEW OF THE CARD BEING SOLD.
          Two cards can share a name and share nothing else: the printing is
          what the value hangs on, so picking the wrong one is the expensive
          mistake this step can make. The seller was being asked to describe,
          price and photograph a card they had only ever seen as one line of a
          search result. This is the picture that confirms the choice, and it
          renders the four facts that separate two printings of one name: the
          art, the set, the collector number and the rarity.

          It is `Gallery` and `Identity` from `patterns/detail`, the same two
          the card detail page builds its hero from, so this is not a third
          card-art treatment. Gallery draws no thumbnail strip below two
          images, which is exactly one here, and it already carries the mobile
          rule that drops the hero to 3/4 at 42% of the column below 1024 — so
          the preview does not eat a phone screen the form still has to fill.

          `as="h3"` because `Identity` defaults its title to `h1` and the panel
          heading above it is the `h2`. */}
      {card ? (
        <div className="dsa-cl-preview">
          <Gallery
            images={[mediaUrl(card.image_url)].filter(Boolean)}
            alt={card.product_name ? `${card.product_name} card art` : 'Card art'}
          />
          <div>
            <Identity
              as="h3"
              set={card.catalog_group}
              name={card.product_name}
              rarity={card.rarity}
              cardNumber={card.card_number}
            />
            <p className="dsk-hint">
              This is the printing your listing will be attached to. If it is not the card in
              your hand, search again.
            </p>
          </div>
        </div>
      ) : cardLoading ? (
        <Spinner center label="Loading the card you picked" />
      ) : null}

      <ModalField label="Condition" hint={conditionHint} error={errors.condition}>
        <ModalSelect
          options={conditionOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
          value={formData.condition}
          onChange={(value) => updateFormData('condition', value)}
        />
      </ModalField>

      {/* Always visible, disabled when the card offers no real choice or while
          the lookup is in flight. A control that is present and honest about
          being unavailable beats one that appears and disappears. */}
      <ModalField label="Finish">
        <ModalSelect
          options={finishOptions.length ? finishOptions : ['Normal']}
          value={formData.finish}
          onChange={(value) => updateFormData('finish', value)}
          disabled={finishDisabled}
        />
      </ModalField>

      <ModalField label="Description" optional>
        <ModalTextarea
          rows={4}
          value={formData.description}
          placeholder="All is well..."
          onChange={(event) => updateFormData('description', event.target.value)}
        />
      </ModalField>
    </WizardPanel>
  );
}

export default CardDetailsStepView;
